<template>
  <teleport to=".nz-modal-body">
    <transition name="modal-fade-scale">
      <div v-if="visible" class="uploader-overlay" style="pointer-events:auto; z-index:999999;">
        <div 
          class="uploader-box"
          :class="{ 'nz-uploading': uploading }"
          >

          <!-- HEADER -->
          <div class="uploader-header">
            <h3>{{ headerTitle }}</h3>
            <button class="btn btn-icon btn-icon-flat" @click="closeUploader">
              <span class="icon-close"></span>
            </button>
          </div>

          <!-- PDF TITLE INPUT (ONLY FOR FILE MODE) -->
          <div v-if="mode === 'file'" class="pdf-title-edit">
            <input
              type="text"
              v-model="localFileTitle"
              placeholder="Title"
              @keydown.enter.prevent="onConfirmPdfTitle"
            />
          </div>

          <!-- DROPZONE -->
          <div
            class="uploader-dropzone"
            @click="!uploading && triggerFileInput()"
            @dragover.prevent
            @drop.prevent="hoverDrop = false; handleDrop($event)"
            @dragenter.prevent="hoverDrop = true"
            @dragleave.prevent="hoverDrop = false"
            :class="{
              hover: hoverDrop && !uploading,
              'nz-loading': uploading
            }"
          >
            <span class="icon-upload"></span>
            <p class="nz-upload-hint">Drop files here or click to upload</p>

            <input
              ref="fileInput"
              type="file"
              :accept="fileAccept"
              hidden
              @change="handleFileSelect"
            />

            <!-- Loading overlay -->
            <div v-if="uploading" class="nz-upload-overlay">
              <div class="nz-upload-progress">
                <div
                  class="nz-upload-bar"
                  :style="{ width: uploadProgress + '%' }"
                ></div>
              </div>
              <p class="nz-upload-title">Uploading ...</p>
              <span class="nz-upload-label">
                {{ uploadProgress }}%
              </span>
            </div>
          </div>

          <!-- FILE LIST -->
          <div class="uploader-list" ref="sortableList">

            <div
              v-for="file in images"
              :key="file.name"
              class="uploader-item"
            >

              <!-- DRAG HANDLE -->
              <div class="drag-handle">
                <span class="icon-move"></span>
              </div>

              <!-- THUMB -->
              <div class="uploader-thumb-wrapper" @click="selectFile(file)">
                <template v-if="mode === 'file'">
                  <!-- PDF ICON -->
                  <span class="icon-file-preview-pdf pdf-icon"></span>
                </template>

                <template v-else>
                  <img
                    class="uploader-thumb"
                    @click="selectImage(file.name)"
                    :src="getThumb(file.name)"
                  />
                </template>
              </div>

              <!-- INFO -->
              <div class="uploader-info">
                <strong>{{ file.name }}</strong>

                <div v-if="mode !== 'file'" class="uploader-size">
                  ({{ file.width }} × {{ file.height }})
                </div>
              </div>

              <!-- ACTION BUTTONS -->
              <div class="uploader-actions">

                <!-- SELECT (SINGLE IMAGE MODE ONLY) -->
                <button
                    v-if="mode === 'single' || mode === 'file'"
                    class="btn btn-icon"
                    :class="{ 'nz-on': isSelected(file.name) }"
                    @click="selectImage(file.name)"
                  >
                  <span class="icon-checkbold"></span>
                </button>

                <!-- CROP (ONLY IMAGES / NOT SVG / NOT PDF) -->
                <button
                  v-if="mode !== 'file' && !isSvg(file.name)"
                  class="btn btn-icon"
                  @click="openCropper(file)"
                >
                  <span class="icon-crop"></span>
                </button>

                <!-- DELETE -->
                <button
                  class="btn btn-icon btn-danger"
                  @click="confirmDelete(file)"
                >
                  <span class="icon-trash"></span>
                </button>

              </div>
            </div>
          </div>

        </div>
      </div>
    </transition>
  </teleport>

  <!-- DELETE CONFIRM -->
  <transition name="fade">
    <div v-if="deleteVisible" class="meta-overlay" @click.self="closeDelete">
      <div class="meta-box">
        <h3>Delete File</h3>
        <p>Really delete <strong>{{ deleteTarget?.name }}</strong>?</p>

        <div class="meta-actions">
          <button class="btn btn-secondary" @click="closeDelete">Cancel</button>
          <button class="btn btn-icon-text btn-danger" @click="performDelete"><span class="icon-trash"></span> Delete</button>
        </div>
      </div>
    </div>
  </transition>

  <!-- CROPPERS -->
  <nz-cropper-vue-advanced
    v-if="activeCropper === 'vue'"
    :src="cropperImage"
    :aspect="cropAspect"
    @close="closeCropper"
    @cropped="onCropFinished"
  />

  <nz-cropper-pintura
    v-if="activeCropper === 'pintura'"
    :src="cropperImage"
    :aspect="cropAspect"
    @close="closeCropper"
    @cropped="onCropFinished"
  />
</template>

<script>
import Sortable from "sortablejs";
import NzCropperVueAdvanced from "@/components/cropper/NzCropperVueAdvanced.vue";
import NzCropperPintura from "@/components/cropper/NzCropperPintura.vue";
import { loadCustomerConfig } from "@/utils/loadCustomerConfig.js";

const API = import.meta.env.VITE_API_URL || '';
//console.log("[DEBUG API]", API);

export default {
  name: "NzUploader",
  components: { NzCropperVueAdvanced, NzCropperPintura },

  props: {
    visible: Boolean,
    selected: String,
    customerId: Number,
    articleId: Number,
    blockname: String,
    brickId: Number,
    lang: String,
    brickImg: {
      type: String,
      default: ""
    },    
    mode: { type: String, default: "single" },
    fileTitle: { type: String, default: "" },
  },

  emits: {
    selected: value => typeof value === "string",
    singleDeleted: value => typeof value === "string",
    galleryUpdated: () => true,
    titleChanged: value => typeof value === "string",
    close: () => true
  },

  data() {
    return {
      images: [],
      hoverDrop: false,

      uploading: false,
      uploadProgress: 0,      

      deleteVisible: false,
      deleteTarget: null,

      localFileTitle: "",

      cropperImage: null,
      cropAspect: null,
      activeCropper: null,
      cropOriginalName: null,
    };
  },

  computed: {
    headerTitle() {
      return this.mode === "file"
        ? "Select File"
        : this.mode === "gallery"
        ? "Manage Gallery"
        : "Select Image";
    },

    fileAccept() {
      return this.mode === "file" ? "application/pdf" : "image/*";
    },

    isSvg() {
      return name => /\.svg$/i.test(name);
    },

    isSelected() {
      return (name) => {
        if (this.mode === "file") {
          // PDF MODE → match selected PDF
          return this.selected === name;

        }
        // SINGLE IMAGE MODE
        return this.mode === "single" && this.selected === name;
      };
    },

  },

  watch: {
    visible(v) {
      if (v) {
        this.localFileTitle = this.fileTitle || "";
        this.loadImages();
        this.$nextTick(() => this.initSortable());
      }
    },
  },

  methods: {
    /* -------------------------------------------------
       CLOSE
    ------------------------------------------------- */
    closeUploader() {
      if (this.mode === "file") {
        this.$emit("titleChanged", this.localFileTitle);
      }
      if( this.mode === "gallery" || this.mode === "file" ) this.$emit("galleryUpdated");
      this.$emit("close");
    },

    onConfirmPdfTitle() {
      this.$emit("titleChanged", this.localFileTitle);
    },

    triggerFileInput() {
      // console.log("triggerFileInput");
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    
    uploadWithProgress(url, form, apiKey) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open("POST", url);

        if (apiKey) {
          xhr.setRequestHeader("x-api-key", apiKey);
        }

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            this.uploadProgress = Math.round((e.loaded / e.total) * 100);
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.responseText);
          } else {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        };

        xhr.onerror = () => reject(new Error("Upload network error"));

        xhr.send(form);
      });
    },  
  
    /* -------------------------------------------------
       LOAD FILES
    ------------------------------------------------- */
    async loadImages() {
      const apiKey = localStorage.getItem("api_key");

      // PDF MODE
      if (this.mode === "file") {
        const q = new URLSearchParams({
          customerId: this.customerId,
          articleId: this.articleId,
          blockname: `${this.blockname}_pdf`,
          lang: this.lang,
        });

        const res = await fetch(`${API}/api/gallery/pdfs?${q}`, {
          headers: { ...(apiKey ? { "x-api-key": apiKey } : {}) },
        });

        const json = await res.json();
        this.images = json.files || [];
        return;
      }

      // GALLERY
      if (this.mode === "gallery") {
        const url = `${API}/api/gallery/${this.customerId}/${this.articleId}/${this.blockname}/${this.lang}`;
        const res = await fetch(url, {
          headers: { ...(apiKey ? { "x-api-key": apiKey } : {}) },
        });
        const json = await res.json();
        this.images = json.entries || [];
        return;
      }

      // SINGLE IMAGE
      const q = new URLSearchParams({
        customerId: this.customerId,
        articleId: this.articleId,
        blockname: this.blockname,
        lang: this.lang,
      });

      const res = await fetch(`${API}/api/upload/images?${q}`, {
        headers: { ...(apiKey ? { "x-api-key": apiKey } : {}) },
      });

      const json = await res.json();
      this.images = json.images || [];
    },

    /* -------------------------------------------------
       THUMB (images only)
    ------------------------------------------------- */
    getThumb(name) {
      return `${API}/uploads/customer_${this.customerId}/articles/id_${this.articleId}/${this.blockname}/thumbnail/${name}?v=${Date.now()}`;
    },

    /* -------------------------------------------------
       UPLOAD
    ------------------------------------------------- */
    async handleDrop(e) {
      const file = e.dataTransfer.files?.[0];
      if (file) await this.handleUpload(file);
    },

    async handleFileSelect(e) {
      const file = e.target.files?.[0];
      if (file) await this.handleUpload(file);
      e.target.value = "";
    },

async handleUpload(file) {
  const apiKey = localStorage.getItem("api_key");
  const form = new FormData();

  this.uploading = true;
  this.uploadProgress = 0;

  try {
    form.append("file", file);
    form.append("customerId", this.customerId);
    form.append("articleId", this.articleId);
    form.append("blockname", this.blockname);
    form.append("brickId", this.brickId);
    form.append("lang", this.lang);

    // PDF
    if (this.mode === "file") {
      form.append("folderType", "pdf");

      await this.uploadWithProgress(
        `${API}/api/upload/pdf`,
        form,
        apiKey
      );

      await this.loadImages();
      return;
    }

    // IMAGES
    form.append(
      "folderType",
      this.mode === "gallery" ? "cpimggallery" : "cpimage"
    );

    await this.uploadWithProgress(
      `${API}/api/upload/image`,
      form,
      apiKey
    );

    await this.loadImages();
  } finally {
    this.uploading = false;
    this.uploadProgress = 0;
  }
},

    /* -------------------------------------------------
       SELECT FILE
    ------------------------------------------------- */
    selectFile(file) {
      if (this.mode === "file") {
        this.$emit("selected", file.name);
        this.closeUploader();
      }
    },

    selectImage(name) {
      this.$emit("selected", name);
      this.closeUploader();
    },

    /* -------------------------------------------------
       DELETE
    ------------------------------------------------- */
    confirmDelete(file) {
      this.deleteTarget = file;
      this.deleteVisible = true;
    },

    closeDelete() {
      this.deleteVisible = false;
    },

    async performDelete() {
      const apiKey = localStorage.getItem("api_key");
      const name = this.deleteTarget.name;

      await fetch(`${API}/api/upload/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(apiKey ? { "x-api-key": apiKey } : {}),
        },
        body: JSON.stringify({
          customerId: this.customerId,
          articleId: this.articleId,
          brickId: this.brickId,
          blockname: this.mode === "file"
            ? `${this.blockname}_pdf`
            : this.blockname,
          lang: this.lang,
          name: name,
        }),
      });

      this.deleteVisible = false;
      await this.loadImages();
      if( this.mode === "gallery" || this.mode === "file" ) this.$emit("gallery-updated");
      if( this.mode === "single" ){
        this.$emit("singleDeleted", name);// check if the image rendered is the image deleted

      }
    },

    /* -------------------------------------------------
       SORTABLE
    ------------------------------------------------- */
    initSortable() {
      if (!this.$refs.sortableList) return;

      Sortable.create(this.$refs.sortableList, {
        animation: 150,
        handle: ".drag-handle",
        onEnd: async () => {
          const ordered = [...this.$refs.sortableList.children].map(
            el => el.querySelector(".uploader-info strong").innerText.trim()
          );

          const apiKey = localStorage.getItem("api_key");

          await fetch(`${API}/api/upload/sort`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(apiKey ? { "x-api-key": apiKey } : {}),
            },
            body: JSON.stringify({
              customerId: this.customerId,
              articleId: this.articleId,
              brickId: this.brickId,
              blockname: this.mode === "file"
                ? `${this.blockname}_pdf`
                : this.blockname,
              lang: this.lang,
              sorted: ordered,
            }),
          });
          this.loadImages();
          if( this.mode === "gallery" || this.mode === "file" ) this.$emit("galleryUpdated");
        },
      });
    },

    /* -------------------------------------------------
       CROPPER
    ------------------------------------------------- */
    async openCropper(file) {
      if (this.mode === "file") return; // PDFs cannot be cropped

      const cfg = await loadCustomerConfig();
      const engine = cfg?.articles?.images?.cropper || "vue";
      this.cropOriginalName = file.name;

      this.cropperImage =
        `${API}/uploads/customer_${this.customerId}/articles/id_${this.articleId}/${this.blockname}/${file.name}`;

      this.cropAspect = cfg?.articles?.images?.aspect || null;
      this.activeCropper = engine === "pintura" ? "pintura" : "vue";
    },

    closeCropper() {
      this.activeCropper = null;
      this.cropperImage = null;
    },

    async onCropFinished(blob) {
      if (!blob) return;

      const apiKey = localStorage.getItem("api_key");
      const form = new FormData();

      form.append("file", blob, this.cropOriginalName);
      form.append("customerId", this.customerId);
      form.append("articleId", this.articleId);
      form.append("blockname", this.blockname);
      form.append("brickId", this.brickId);
      form.append("lang", this.lang);
      form.append("folderType", "cpimage");
      form.append("overwrite", "true");
      form.append("name", this.cropOriginalName);

      await fetch(`${API}/api/upload/image`, {
        method: "POST",
        headers: { ...(apiKey ? { "x-api-key": apiKey } : {}) },
        body: form,
      });

      this.closeCropper();
      await this.loadImages();
      if( this.mode === "gallery" || this.mode === "file" ) this.$emit("galleryUpdated");
    },
  },
};
</script>

<style lang="less">
.uploader-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* LIST */
.uploader-list {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
  opacity: 1;
  /* NEW: height constraint */
  max-height: 70vh;
  overflow-y: auto;

  /* Optional: smooth scrolling and inner padding */
  padding-right: 6px;
  transition: opacity .3s ease-out;

}

.uploader-item {
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 8px;
  display: flex;
  gap: 16px;
  align-items: center;
  background: #fbfbfb;
}

.uploader-thumb {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
}

.uploader-info {
  flex: 1;
  font-size: 14px;
}

.uploader-actions {
  display: flex;
  gap: 8px;
}

.icon-upload{
  font-size: 2.5rem;
  color: @gray;
}

.brick-image{
  max-width: 600px;
  cursor: pointer;

}

.uploader-box {
  background: #fff;
  width: 90%;
  max-width: 720px;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 6px 22px rgba(0,0,0,.3);
}
.uploader-box.nz-uploading {

  pointer-events: none;
  .uploader-list{
    opacity: .5;
  }

}
/* HEADER */
.uploader-header, .meta-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  h3{
    padding: 0;
    margin: 0px 0px 18px 0px;
  }
  button.btn-icon{
    transform: translate(8px, -8px);
  }
}

.drag-handle{
  cursor: grab;
  span{
    font-size: 1.5rem;
    color: @gray-darker;
    transition: color .3s ease;

  }
  &:hover{
    span{
      color: @accent;
    }
  }
}

/* Metadata Modal */
.meta-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999;
}

.meta-box {
  width: 420px;
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  animation: metaPop .25s ease;
}

.meta-tabs {
  display: flex;
  gap: 6px;
  margin: 1rem 0;
}

.meta-tabs button {
  padding: 6px 12px;
  border-radius: 6px;
  background: #eee;
  border: none;
  cursor: pointer;
}

.meta-tabs .active {
  background: @primary;
  color: #fff;
}

.meta-preview {
  text-align: center;
  margin-bottom: 1rem;

  img {
    max-width: 180px;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(0,0,0,.2);
  }
}

.meta-body{
  min-height: 400px;
}

.meta-body label {
  display: block;
  font-size: 13px;
  margin-bottom: 4px;
}

.meta-body input,
.meta-body textarea {
  width: 100%;
  margin-bottom: 1rem;
}

.meta-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes metaPop {
  from { transform: scale(.92); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

.title-edit{
  padding: 0px;

  input{
      width: 400px;
  }

}


/* DROPZONE */
.uploader-dropzone {
  position: relative;
  height: 130px;
  min-height: 130px;
  border: 2px dashed #bbb;
  padding: 1.5rem;
  margin-top: 1rem;
  text-align: center;
  border-radius: 8px;
  cursor: pointer;
  p.nz-upload-hint{
    font-size: 1rem;
    position: absolute;
    top: 40%;
    left: 50%;
    transform: translateX(-50%);
  }
  span.icon-upload{
    position: absolute;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);

  }
  &:hover {
    border-color: @accent;
    background: #f1faff;
    span.icon-upload{
      color: @accent !important;
    }
  }
}

.uploader-dropzone.nz-loading {
  pointer-events: none;
  height: 130px !important;
  min-height: 130px !important;  
  opacity: 0.7;
  background-image: none !important;
  background-color: #fff !important;

  span.icon-upload, p.nz-upload-hint{
    visibility: hidden;
  }
}

.nz-upload-progress {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  height: 6px;
  background: rgba(0,0,0,0.1);
  border-radius: 4px;
  overflow: hidden;
}

.nz-upload-bar {
  position: absolute;
  height: 100%;
  background: linear-gradient(90deg, @accent-weak, @accent);
  transition: width 0.2s ease;
}

.nz-upload-title{
  position: absolute;
  top: 8%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1rem;
  color: @accent;
  text-transform: uppercase;

}
.nz-upload-label {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1.5rem;
  color: @accent;

}


</style>
